import React, { createContext, useEffect, useReducer } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  loginUser,
  registerUser,
  registerSeller as registerSellerService,
  loginGoogle,
  logoutUser,
  getCompanyByEmail,
  resetPassword,
  confirmEmail,
  forgotPassword as forgotPasswordService
} from '../services/authService';
import axios from '../utils/axios';

// Action types
const LOGIN = 'LOGIN';
const LOGOUT = 'LOGOUT';
const INITIALIZE = 'INITIALIZE';

// Initial state
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// Reducer
const accountReducer = (state, action) => {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        isLoggedIn: true,
        user: action.payload.user,
        isInitialized: true
      };
    case LOGOUT:
      return {
        ...state,
        isLoggedIn: false,
        user: null,
        isInitialized: true
      };
    case INITIALIZE:
      return {
        ...state,
        isLoggedIn: action.payload.isLoggedIn,
        user: action.payload.user,
        isInitialized: true
      };
    default:
      return state;
  }
};

// Token utils
const verifyToken = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const setSession = (token, user) => {
  if (token && user) {
    localStorage.setItem('serviceToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem('pageCompany_app_config');
  }
};

// Context
const JWTContext = createContext(null);

// Provider
export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  // Initialization on mount
  // useEffect(() => {

  //   const token = localStorage.getItem('serviceToken');
  //   const user = localStorage.getItem('user');

  //   if (user !== null && (token && verifyToken(token))) {
  //     const decoded = jwtDecode(token);
  //     const userData = user ? JSON.parse(user) : null;
  //     const expInMs = decoded.exp * 1000;
  //     const now = Date.now();

  //     const timeUntilExpiration = expInMs - now;

  //     if (timeUntilExpiration <= 0) {
  //       logout();
  //       return;
  //     }

  //     const timer = setTimeout(() => {
  //       logout();
  //     }, timeUntilExpiration);

  //     dispatch({ type: INITIALIZE, payload: { isLoggedIn: true, user: userData } });

  //     return () => clearTimeout(timer);
  //   } else {
  //     setSession(null, null);
  //     dispatch({ type: LOGOUT });
  //   }
  // }, [state.isLoggedIn]);

  useEffect(() => {
    console.log('useEffect: Initializing JWTContext');
    const initialize = () => {
      const token = localStorage.getItem('serviceToken');
      const user = localStorage.getItem('user');

      // console.log('useEffect: Checking localStorage', { token, user });

      if (token && user && verifyToken(token)) {
        const userData = user ? JSON.parse(user) : null;
        const decoded = jwtDecode(token);
        const expInMs = decoded.exp * 1000;
        const timeUntilExpiration = expInMs - Date.now();

        // console.log('useEffect: Token valid, setting state', { userData, timeUntilExpiration });

        dispatch({
          type: INITIALIZE,
          payload: { isLoggedIn: true, user: userData }
        });

        // Set logout timer
        if (timeUntilExpiration > 0) {
          const timer = setTimeout(() => {
            console.log('useEffect: Token expired, logging out');
            logout();
          }, timeUntilExpiration);
          return () => clearTimeout(timer);
        } else {
          console.log('useEffect: Token already expired, logging out');
          logout();
        }
      } else {
        console.log('useEffect: No valid token or user, logging out');
        setSession(null, null);
        dispatch({ type: INITIALIZE, payload: { isLoggedIn: false, user: null } });
      }
    };

    initialize();
  }, []); // Empty dependencies to run only on mount

  // Auth functions
  const login = async (data) => {
    const response = await loginUser(data);
    const token = response.token;
    const user = response.user;

    setSession(token, user); // saves to localStorage and sets axios header
    dispatch({ type: LOGIN, payload: { user } });

   return {user, token};
  };

  

  const loginWithGoogle = async (accessToken) => {
    try {
      const response = await loginGoogle(accessToken); // Call your authService loginGoogle function
      const { token, user } = response;
      setSession(token, user); // Save token and user to localStorage and set axios header
      dispatch({ type: LOGIN, payload: { user } });
      return { success: true };
    } catch (err) {
      console.error("Login with Google error:", err);
      throw new Error(
        err.response?.data?.message || 
        err.message || 
        'Google login failed. Please try again later.'
      );
    }
  };

  const register = async (data) => {
    const response = await registerUser(data);
    return response;
    // setSession(token, user);
    // dispatch({ type: LOGIN, payload: { user } });
  };

   const registerSeller = async (data) => {
    const response = await registerSellerService(data);
    return response;
    // setSession(token, user);
    // dispatch({ type: LOGIN, payload: { user } });
  };

  
  const forgotPassword = async (email) => {
    const response = await forgotPasswordService(email);
    return response;
    // setSession(token, user);
    // dispatch({ type: LOGIN, payload: { user } });
  };

  const logout = () => {
    logoutUser();
    dispatch({ type: LOGOUT });
  };

  // Other utils
  const resetPasswordHandler = async (token, username, password, confirmPassword) => {
    return await resetPassword(token, username, password, confirmPassword);
  };

  const confirmEmailHandler = async (token, username) => {
    return await confirmEmail(token, username);
  };

  const getCompanyByEmailHandler = async (email) => {
    return await getCompanyByEmail(email);
  };

  // console.log('JWTContext initialized', state);

  // Fallback loader
  if (!state.isInitialized) return <div>Loading...</div>;

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        loginWithGoogle,
        logout,
        register,
        registerSeller,
        forgotPassword,
        resetPassword: resetPasswordHandler,
        confirmEmail: confirmEmailHandler,
        getCompanyByEmail: getCompanyByEmailHandler
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
